
import { useState } from "react";
import { mastersApi } from "@/lib/api";
import { Search, CheckCircle2, XCircle, Users, MapPin, CalendarDays, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { formatDate, localToday } from "@/lib/dateFormat";

interface VenueConflict {
  event_id: number;
  event_name: string;
  client_name: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string;
}

interface VenueAvailability {
  id: number;
  name: string;
  capacity: number | null;
  location: string | null;
  status: "available" | "occupied";
  conflict: VenueConflict | null;
}

interface AvailabilityResult {
  venues: VenueAvailability[];
  start_date: string;
  end_date: string;
  available_count: number;
  occupied_count: number;
}

interface AvailabilityCheckerProps {
  /** Pre-fill dates from the parent (e.g. calendar click) */
  presetStartDate?: string;
  presetEndDate?: string;
  /** Called when user clicks "Create Event" on an available venue */
  onCreateEvent?: (venueId: number, venueName: string, startDate: string, endDate: string) => void;
}

/** Today's date in local time (toISOString would give yesterday before 05:30 IST). */
function today() {
  return localToday();
}

export default function AvailabilityChecker({
  presetStartDate,
  presetEndDate,
  onCreateEvent,
}: AvailabilityCheckerProps) {
  const [startDate, setStartDate] = useState(presetStartDate ?? today());
  const [endDate, setEndDate] = useState(presetEndDate ?? today());
  const [result, setResult] = useState<AvailabilityResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedVenue, setExpandedVenue] = useState<number | null>(null);

  const handleCheck = async () => {
    if (!startDate || !endDate) return;
    if (endDate < startDate) {
      setError("End date cannot be before start date.");
      return;
    }
    setError(null);
    setLoading(true);
    setResult(null);
    setExpandedVenue(null);
    try {
      const res = await mastersApi.venues.checkAllAvailability(startDate, endDate);
      setResult(res.data?.data ?? res.data);
    } catch {
      setError("Failed to check availability. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (val: string) => {
    setStartDate(val);
    if (endDate && val > endDate) setEndDate(val);
    setResult(null);
  };

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 md:p-5 space-y-4">
      {/* Title */}
      <div className="flex items-center gap-2">
        <CalendarDays className="w-5 h-5 text-amber-500" />
        <h3 className="font-bold text-stone-800 text-sm md:text-base">Check Availability</h3>
        <span className="text-xs text-stone-400 ml-auto">Select dates to see venue status</span>
      </div>

      {/* Date Inputs */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-stone-500 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => handleStartDateChange(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-white"
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-stone-500 mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={(e) => { setEndDate(e.target.value); setResult(null); }}
            className="w-full px-3 py-2.5 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-white"
          />
        </div>
        <div className="sm:self-end">
          <button
            onClick={handleCheck}
            disabled={loading || !startDate || !endDate}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            {loading ? "Checking…" : "Check"}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-3">
          {/* Summary pills */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-stone-500">
              {formatDate(result.start_date)}
              {result.start_date !== result.end_date && ` – ${formatDate(result.end_date)}`}
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <span className="flex items-center gap-1 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-semibold text-emerald-700">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {result.available_count} Available
              </span>
              <span className="flex items-center gap-1 px-2.5 py-1 bg-red-50 border border-red-200 rounded-full text-xs font-semibold text-red-700">
                <XCircle className="w-3.5 h-3.5" />
                {result.occupied_count} Occupied
              </span>
            </div>
          </div>

          {result.venues.length === 0 ? (
            <p className="text-sm text-stone-400 text-center py-6">No active venues found.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {result.venues.map((venue) => {
                const isAvailable = venue.status === "available";
                const isExpanded = expandedVenue === venue.id;

                return (
                  <div
                    key={venue.id}
                    className={`rounded-xl border p-3.5 transition-all ${
                      isAvailable
                        ? "border-emerald-200 bg-emerald-50"
                        : "border-red-200 bg-red-50"
                    }`}
                  >
                    {/* Venue header */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {isAvailable ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          )}
                          <p className="font-semibold text-stone-800 text-sm truncate">{venue.name}</p>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 ml-6">
                          {venue.capacity && (
                            <span className="flex items-center gap-1 text-xs text-stone-500">
                              <Users className="w-3 h-3" />
                              {venue.capacity.toLocaleString("en-IN")} guests
                            </span>
                          )}
                          {venue.location && (
                            <span className="flex items-center gap-1 text-xs text-stone-500">
                              <MapPin className="w-3 h-3" />
                              {venue.location}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Status badge */}
                      <span
                        className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${
                          isAvailable
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {isAvailable ? "Free" : "Booked"}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="mt-2.5 ml-6 flex items-center gap-2">
                      {isAvailable && onCreateEvent && (
                        <button
                          onClick={() => onCreateEvent(venue.id, venue.name, startDate, endDate)}
                          className="text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          + Create Event
                        </button>
                      )}
                      {!isAvailable && venue.conflict && (
                        <button
                          onClick={() => setExpandedVenue(isExpanded ? null : venue.id)}
                          className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium"
                        >
                          {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          {isExpanded ? "Hide details" : "View conflict"}
                        </button>
                      )}
                    </div>

                    {/* Conflict details panel */}
                    {!isAvailable && venue.conflict && isExpanded && (
                      <div className="mt-2.5 ml-6 p-3 bg-white border border-red-200 rounded-lg space-y-1.5">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-stone-800 truncate">
                            {venue.conflict.event_name}
                          </p>
                          <Link
                            to={`/events/detail?id=${venue.conflict.event_id}`}
                            className="flex-shrink-0 inline-flex items-center gap-0.5 h-10 px-2 -my-2 text-xs text-amber-600 hover:text-amber-700 font-medium ml-2"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View
                          </Link>
                        </div>
                        {venue.conflict.client_name && (
                          <p className="text-xs text-stone-500">
                            Client: <span className="font-medium text-stone-700">{venue.conflict.client_name}</span>
                          </p>
                        )}
                        <p className="text-xs text-stone-500">
                          Dates:{" "}
                          <span className="font-medium text-stone-700">
                            {formatDate(venue.conflict.start_date)}
                            {venue.conflict.end_date && venue.conflict.end_date !== venue.conflict.start_date
                              ? ` – ${formatDate(venue.conflict.end_date)}`
                              : ""}
                          </span>
                        </p>
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                            venue.conflict.status === "confirmed"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {venue.conflict.status}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
